import { inject, Injectable, OnDestroy } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { completeSubject } from 'src/app/common/app.helpers';
import { ProfileFormTypes } from 'src/app/forms/forms.enum';
import { RoleInfo } from 'src/app/models/role-info.model';
import { FormStoreService } from 'src/app/services/form-store.service';
import { FieldAttributeConstructorService } from '../../../../forms/fields/field-attributes-constructor.service';
import { FormsAttributeService } from '../../../../forms/forms-attribute.service';
import {
  Attribute,
  EditProfileFormContent,
  FieldAttribute,
  FormConstructorContent,
  UserTypeComponentContent,
} from '../../../../interfaces/form-and-field.interface';
import { EditProfileChangesContent, GetComponentContent } from '../../../../interfaces/profile.interface';
import { EditProfileParts, IndividualRoles, ProfileTypes } from '../../profile.enum';
import { ProfileService } from '../../profile.service';
import { aliasMetadata, organizationNameMetadata } from './profile-forms.metadata';

@Injectable()
export class FormContentService implements OnDestroy {
  private formContentChanges: Subject<EditProfileChangesContent> = new Subject();
  private profileService = inject(ProfileService);
  private sessionService = inject(SessionService);
  private fieldsService = inject(FieldAttributeConstructorService);
  private formsAttributeService = inject(FormsAttributeService);
  private formStoreService = inject(FormStoreService);

  public ngOnDestroy(): void {
    completeSubject(this.formContentChanges);
  }

  public get changes(): Observable<EditProfileChangesContent> {
    return this.formContentChanges.asObservable();
  }

  public notifyFormChangesSubscribers(formContent: FormConstructorContent[]): void {
    this.formContentChanges.next({
      data: formContent,
      formType: EditProfileParts.ON_REMOVED_ROLES,
    });
  }

  public getBaseFormContent(): FormConstructorContent {
    const attrs: Attribute[] =
      this.sessionService.user.profile.verified_email !== this.sessionService.user.profile.email
        ? this.formStoreService.getMetadata(ProfileTypes.MAIN_WITH_WARNING)
        : this.formStoreService.getMetadata(ProfileTypes.MAIN);

    return this.getFormContent({
      formType: ProfileFormTypes.PROFILE,
      attrs: this.adjustBaseFormPerUserType(attrs),
      data: this.concatBaseProfileData(),
      profileType: ProfileTypes.MAIN,
    });
  }

  public getOrganizationFormsContent(): void {
    this.setupUserTypeComponentContent({
      formType: ProfileFormTypes.ORGANIZATION,
      data: this.profileService.userInfo.organization || {},
    });
  }

  public getIndividualFormsContent(): void {
    if (this.sessionService.isIncompleteIndividual()) {
      return;
    }

    this.getBaseIndividualTypeContent();

    const rolesInfo: RoleInfo[] = this.profileService.userRolesInfo;
    if (rolesInfo.length > 0) {
      this.formContentChanges.next({
        data: this.initSavedRolesFormContent(rolesInfo, []),
        formType: EditProfileParts.ROLES,
      });
    }
  }

  public getRoleFormContent(): void {
    if (this.sessionService.hasNoUserType || this.sessionService.isIncompleteIndividual()) {
      this.getBaseIndividualTypeContent();
    }
  }

  public addUserRole(role: string): void {
    const attrs: Attribute[] = this.formStoreService.getMetadata(role);
    this.profileService.addUserRole(new RoleInfo(role, IndividualRoles[role] as string, false, true));

    this.formContentChanges.next({
      data: this.getFormContent({
        formType: role,
        attrs,
        data: {},
        profileType: '',
      }),
      formType: EditProfileParts.ROLE,
    });
  }

  public removeUserRole(role: string): void {
    const attrs: Attribute[] = this.formStoreService.getMetadata(role);
    this.profileService.removeUserRole(new RoleInfo(role, IndividualRoles[role] as string, false, true));

    this.formContentChanges.next({
      data: this.getFormContent({
        formType: role,
        attrs,
        data: {},
        profileType: '',
      }),
      formType: EditProfileParts.ON_REMOVED_ROLE,
    });
  }

  private getFormContent(content: EditProfileFormContent): FormConstructorContent {
    return {
      formAttribute: this.formsAttributeService.getFormAttribute(content.formType, content.profileType),
      fieldAttributes:
        content.profileType === ProfileTypes.MAIN
          ? this.getBaseFormAttributesMetadata(content.attrs)
          : this.fieldsService.convertAttributesMetadata(content.attrs, ProfileFormTypes.PROFILE),
      data: content.data,
    };
  }

  private getBaseFormAttributesMetadata(attrs: Attribute[]): FieldAttribute[] {
    return this.fieldsService.convertAttributesMetadata(attrs, ProfileFormTypes.PROFILE);
  }

  private concatBaseProfileData(): object {
    const baseData: object = {};
    baseData['verified_email'] = this.sessionService.user.profile.verified_email;
    if (this.sessionService.user.profile.verified_email !== this.sessionService.user.profile.email) {
      baseData['email'] = this.sessionService.user.profile.email;
    }
    baseData['image_url'] = this.sessionService.user.image_url;
    if (this.sessionService.user.organization) {
      baseData['organization_name'] = this.sessionService.user.displayed_name;
    } else {
      baseData['alias'] = this.sessionService.user.displayed_name;
    }
    baseData['phone_number'] = this.sessionService.user.profile.phone_number;
    baseData['has_bound_authenticator'] = this.sessionService.user.authorisation_settings?.has_bound_authenticator;
    return baseData;
  }

  private adjustBaseFormPerUserType(attrs: Attribute[]): Attribute[] {
    return attrs.filter((attr: Attribute) => {
      return this.sessionService.user.organization
        ? attr.name !== aliasMetadata.name
        : attr.name !== organizationNameMetadata.name;
    });
  }

  private setupUserTypeComponentContent(content: UserTypeComponentContent): void {
    const metadata: Attribute[] = this.formStoreService.getMetadata(content.formType);
    const additionalFieldsMetadata: ProfileTypes = this.getAdditionalFieldsMetadata(content.formType);

    this.notifyAboutUserTypeContent({
      formType: content.formType,
      data: content.data,
      attrs: metadata,
      profileAttrs: this.formStoreService.getMetadata(additionalFieldsMetadata),
    });
  }

  private getAdditionalFieldsMetadata(formType: string): ProfileTypes {
    return formType === ProfileFormTypes.ORGANIZATION
      ? ProfileTypes.ADDITIONAL_WITH_FULL_ADDRESS_LOCATION
      : ProfileTypes.ADDITIONAL;
  }

  private getBaseIndividualTypeContent(): void {
    this.setupUserTypeComponentContent({
      formType: ProfileFormTypes.INDIVIDUAL,
      data: this.profileService.userInfo.individual || {},
    });
  }

  private getProfileFormContent(attrs: Attribute[], profileType: string): FormConstructorContent {
    return this.getFormContent({
      formType: ProfileFormTypes.PROFILE,
      attrs,
      data: this.profileService.userInfo.profile,
      profileType,
    });
  }

  private initSavedRolesFormContent(rolesInfo: RoleInfo[], data: FormConstructorContent[]): FormConstructorContent[] {
    rolesInfo.forEach((roleInfo: RoleInfo) => {
      data.push(
        this.getFormContent({
          formType: roleInfo.value,
          attrs: this.formStoreService.getMetadata(roleInfo.value),
          data: this.profileService.userInfo[roleInfo.value] as object,
          profileType: '',
        }),
      );
    });
    return data;
  }

  private notifyAboutUserTypeContent(content: GetComponentContent): void {
    this.formContentChanges.next({
      data: [
        this.getFormContent({
          formType: content.formType,
          attrs: content.attrs,
          data: content.data,
          profileType: '',
        }),
        this.getProfileFormContent(content.profileAttrs, ProfileTypes.ADDITIONAL),
      ],
      formType: EditProfileParts.USER_TYPE,
    });
  }
}
